// ─────────────────────────────────────────────────────────────
//  Billing-Core – EINZIGE QUELLE DER WAHRHEIT für die Abrechnungs-
//  und Steuerlogik (Beträge, Brutto/Netto, Fälligkeiten).
//
//  Reines CommonJS ohne Abhängigkeiten, damit es BEIDE Welten nutzen
//  können:
//    • Frontend  -> src/Admin/paymentEngine.js importiert dieses Modul
//                   (Re-Export der öffentlichen API).
//    • Cloud Fn  -> functions/index.js erhält eine 1:1-Kopie dieser
//                   Datei (functions/billingCore.js), automatisch
//                   gespiegelt über den predeploy-Hook in firebase.json
//                   ("cp src/Admin/billingCore.js functions/billingCore.js").
//
//  >>> NUR DIESE DATEI BEARBEITEN. Die Kopie in functions/ wird beim
//  >>> Deploy automatisch überschrieben – niemals dort von Hand ändern.
//
//  Beträge in EUR. monthlyAmount/setupFee sind IMMER NETTO. Der
//  Rechnungsbetrag (amount) ist BRUTTO – abhängig vom Steuermodus:
//    reverse_charge -> EU-B2B, keine IVA, Brutto = Netto
//    vat            -> IVA (vatRate %, ES-Standard 21%) auf den Netto
//    none           -> keine IVA, Brutto = Netto
//
//  Die gestaffelte Provisionslogik (BonusClub-Vergütungsplan) liegt in
//  ./commissionCore.js und wird hier für Auszahlungen mitgenutzt.
// ─────────────────────────────────────────────────────────────

const commission = require('./commissionCore');

// ── Steuer / Brutto-Netto (spanische SL → IVA) ─────────────────
const DEFAULT_TAX_MODE = 'reverse_charge';
const DEFAULT_VAT_RATE = 21; // ES IVA general

const TAX_MODES = {
  reverse_charge: {
    id: 'reverse_charge',
    label: 'Reverse Charge (EU-B2B)',
    badge: 'Reverse Charge',
    hasVat: false,
    note: 'EU-B2B-Leistung: Steuerschuldnerschaft des Leistungsempfängers (inversión del sujeto pasivo). Es wird keine IVA ausgewiesen – Brutto = Netto.',
  },
  vat: {
    id: 'vat',
    label: 'IVA ausweisen (Spanien)',
    badge: 'inkl. IVA',
    hasVat: true,
    note: 'Spanische IVA wird ausgewiesen und auf den Nettobetrag aufgeschlagen – Brutto = Netto + IVA.',
  },
  none: {
    id: 'none',
    label: 'Keine IVA',
    badge: 'ohne IVA',
    hasVat: false,
    note: 'Keine IVA ausgewiesen (Sonderfall) – Brutto = Netto.',
  },
};

const taxModeInfo = (mode) => TAX_MODES[mode] || TAX_MODES[DEFAULT_TAX_MODE];

// Effektiver USt-Satz eines Vertrags (0 außer bei taxMode 'vat' mit gültigem Satz).
const effectiveVatRate = (contract) => {
  if (!contract) return 0;
  if (!taxModeInfo(contract.taxMode).hasVat) return 0;
  const r = Number(contract.vatRate);
  return isFinite(r) && r > 0 ? r : 0;
};

// Brutto/Netto-Aufstellung für einen Nettobetrag unter den Vertragssteuerregeln.
const taxBreakdown = (net, contract) => {
  const n = Number(net) || 0;
  const rate = effectiveVatRate(contract);
  const vat = n * (rate / 100);
  return {
    net: n,
    rate,
    vat,
    gross: n + vat,
    mode: taxModeInfo(contract && contract.taxMode).id,
    reverseCharge: (contract && contract.taxMode ? contract.taxMode : DEFAULT_TAX_MODE) === 'reverse_charge',
  };
};

const grossFromNet = (net, contract) => taxBreakdown(net, contract).gross;

// ── Automatische Steuer-Erkennung anhand des Kundenlands ───────
// Rechnungssteller = spanische SL. Aus dem Land des Kunden folgt die korrekte
// umsatzsteuerliche Behandlung – das System wählt den Steuermodus automatisch:
//   ES         -> IVA ausweisen (21%)          (Inland)
//   EU (≠ ES)  -> Reverse Charge               (innergemeinschaftliche B2B-Leistung)
//   Nicht-EU   -> keine IVA                     (Drittland / außerhalb IVA-Anwendung)
const SELLER_COUNTRY = 'ES';

const EU_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'SE', 'ES',
];

const COUNTRY_NAMES = {
  ES: 'Spanien', DE: 'Deutschland', AT: 'Österreich', CH: 'Schweiz', FR: 'Frankreich',
  IT: 'Italien', NL: 'Niederlande', BE: 'Belgien', PT: 'Portugal', IE: 'Irland',
  PL: 'Polen', SE: 'Schweden', DK: 'Dänemark', FI: 'Finnland', GR: 'Griechenland',
  CZ: 'Tschechien', SK: 'Slowakei', HU: 'Ungarn', RO: 'Rumänien', BG: 'Bulgarien',
  HR: 'Kroatien', SI: 'Slowenien', LT: 'Litauen', LV: 'Lettland', EE: 'Estland',
  LU: 'Luxemburg', MT: 'Malta', CY: 'Zypern', GB: 'Vereinigtes Königreich',
  US: 'USA', CA: 'Kanada', AU: 'Australien',
  OTHER_EU: 'Anderes EU-Land', OTHER_NON_EU: 'Anderes Land (Nicht-EU)',
};

const countryName = (code) => COUNTRY_NAMES[String(code || '').toUpperCase()] || code || '';

const isEuCountry = (code) => {
  const c = String(code || '').toUpperCase();
  return c === 'OTHER_EU' || EU_COUNTRIES.includes(c);
};

// Steuermodus + Satz aus dem Kundenland ableiten (Seller = spanische SL).
// detected=false bedeutet: kein Land bekannt -> sicherer Default (Reverse Charge),
// das Land sollte aber bestätigt werden.
function taxModeForCountry(country) {
  const code = String(country || '').toUpperCase();
  if (!code) return { taxMode: DEFAULT_TAX_MODE, vatRate: 0, detected: false };
  if (code === SELLER_COUNTRY) return { taxMode: 'vat', vatRate: DEFAULT_VAT_RATE, detected: true };
  if (isEuCountry(code)) return { taxMode: 'reverse_charge', vatRate: 0, detected: true };
  return { taxMode: 'none', vatRate: 0, detected: true };
}

// Deterministische ID einer Zahlung (idempotent über Frontend + Cloud Function).
const paymentId = (slug, periodKey) => `${slug}__${periodKey}`;

// Fälligkeitsdatum für die i-te Monatsperiode ab Vertragsstart.
// Klemmt den Abrechnungstag an die Monatslänge (kein Überlauf bei 29./30./31.),
// damit jeder Kalendermonat genau eine Periode bekommt (kein Skip, kein Duplikat).
const dueDateForOffset = (startMs, i) => {
  const start = new Date(startMs);
  const billingDay = start.getDate();
  const mIndex = start.getMonth() + i;
  const year = start.getFullYear() + Math.floor(mIndex / 12);
  const month = ((mIndex % 12) + 12) % 12;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const day = Math.min(billingDay, daysInMonth);
  return { ms: new Date(year, month, day, 9, 0, 0, 0).getTime(), periodKey: `${year}-${String(month + 1).padStart(2, '0')}` };
};

// Volle Monate zwischen zwei Zeitpunkten (für "läuft seit X Monaten" / Restlaufzeit).
const monthsElapsed = (startMs, nowMs = Date.now()) => {
  if (!startMs) return 0;
  const s = new Date(startMs);
  const n = new Date(nowMs);
  let m = (n.getFullYear() - s.getFullYear()) * 12 + (n.getMonth() - s.getMonth());
  if (n.getDate() < s.getDate()) m -= 1;
  return Math.max(0, m);
};

// Erzeugt dieser Vertrag überhaupt monatliche Perioden? Ja, sobald es einen
// Retainer gibt ODER das Provisionsmodell aktiv ist – bei reiner Bezahlung je
// Termin ist monthlyAmount 0, die Monatsrechnung entsteht trotzdem und wird
// später um die abgerechneten Termine ergänzt.
function hasRecurringBilling(contract) {
  if (!contract) return false;
  return (Number(contract.monthlyAmount) || 0) > 0 || commission.commissionActive(contract);
}

// Alle bis JETZT fälligen Perioden eines Vertrags (Setup + monatlich).
// Liefert NUR Einträge mit dueDate <= nowMs (also tatsächlich fällig).
function billingPeriods(contract, nowMs = Date.now()) {
  const out = [];
  if (!contract || !contract.active || !contract.startAt) return out;
  const monthly = Number(contract.monthlyAmount) || 0;
  const setup = Number(contract.setupFee) || 0;
  const rate = effectiveVatRate(contract);
  const taxMode = taxModeInfo(contract.taxMode).id;
  // amount = BRUTTO (Rechnungsbetrag); net/vat zusätzlich für die Aufschlüsselung.
  const withTax = (net) => {
    const vat = net * (rate / 100);
    return { amount: net + vat, net, vat, vatRate: rate, taxMode };
  };

  if (setup > 0 && contract.startAt <= nowMs) {
    out.push({ periodKey: 'setup', type: 'setup', dueDate: contract.startAt, ...withTax(setup) });
  }
  if (hasRecurringBilling(contract)) {
    const maxMonths = contract.durationMonths > 0 ? contract.durationMonths : 600;
    for (let i = 0; i < maxMonths; i++) {
      const { ms, periodKey } = dueDateForOffset(contract.startAt, i);
      if (ms > nowMs) break;
      out.push({ periodKey, type: 'recurring', dueDate: ms, ...withTax(monthly) });
    }
  }
  return out;
}

// Nächste noch NICHT fällige Periode (für "nächste Rechnung am ...").
function nextDuePeriod(contract, nowMs = Date.now()) {
  if (!contract || !contract.active || !contract.startAt) return null;
  if (!hasRecurringBilling(contract)) return null;
  const monthly = Number(contract.monthlyAmount) || 0;
  const rate = effectiveVatRate(contract);
  const taxMode = taxModeInfo(contract.taxMode).id;
  const vat = monthly * (rate / 100);
  const maxMonths = contract.durationMonths > 0 ? contract.durationMonths : 600;
  for (let i = 0; i < maxMonths; i++) {
    const { ms, periodKey } = dueDateForOffset(contract.startAt, i);
    if (ms > nowMs) {
      return { periodKey, type: 'recurring', dueDate: ms, amount: monthly + vat, net: monthly, vat, vatRate: rate, taxMode };
    }
  }
  return null;
}

// Läuft der Vertrag (Laufzeit noch nicht abgelaufen)?
function contractRunning(contract, nowMs = Date.now()) {
  if (!contract || !contract.active || !contract.startAt) return false;
  if (!contract.durationMonths || contract.durationMonths <= 0) return true; // unbegrenzt
  return monthsElapsed(contract.startAt, nowMs) < contract.durationMonths;
}

// ── Auszahlungen ans Team (Provision / Fixvergütung) ───────────
// Spiegelbild der Einnahmenlogik: Jeder einem Kunden zugewiesene Mitarbeiter
// verursacht – parallel zur monatlichen Kundenrechnung – eine monatliche
// Auszahlung (Kosten). Beträge basieren auf demselben monthlyAmount des Vertrags.

// Monatliche Auszahlung an ein Mitglied bei gegebenem Kunden-Monatswert (NETTO).
// Fixvergütung -> fester Betrag, sonst Provision = monthly * commissionPercent%.
function memberMonthlyPayout(member, monthly) {
  if (!member) return 0;
  if (member.payType === 'fixed') return Number(member.fixedAmount) || 0;
  return (Number(monthly) || 0) * ((Number(member.commissionPercent) || 0) / 100);
}

// Läuft die Provision/Vergütung dieses Mitglieds für diesen Vertrag noch?
// Begrenzt durch member.durationMonths (0 = laufend), gerechnet ab Vertragsstart.
function payoutRemainingMonths(member, contract, nowMs = Date.now()) {
  const dur = Number(member && member.durationMonths) || 0;
  if (dur <= 0) return Infinity; // laufend
  const elapsed = monthsElapsed(contract && contract.startAt, nowMs);
  return Math.max(0, dur - elapsed);
}

// Deterministische ID einer Auszahlung (idempotent über Frontend + Cloud Fn).
const payoutId = (memberId, slug, periodKey) => `${memberId}__${slug}__${periodKey}`;

// Alle bis JETZT fälligen monatlichen Auszahlungen für ein Mitglied an EINEM
// Vertrag (Kunde). Spiegelt billingPeriods (nur die wiederkehrenden Perioden):
//   • nur solange Vertrag aktiv ist und der Abrechnungstag erreicht wurde
//   • begrenzt durch min(Vertragslaufzeit, member.durationMonths)
//   • optional erst ab sinceMs (Zuweisungsdatum) – keine rückwirkenden Kosten
//     für Monate, in denen das Mitglied dem Kunden noch nicht zugewiesen war.
function payoutPeriods(member, contract, nowMs = Date.now(), sinceMs = 0) {
  const out = [];
  if (!member || member.active === false) return out;
  if (!contract || !contract.active || !contract.startAt) return out;
  const amount = memberMonthlyPayout(member, contract.monthlyAmount);
  if (!(amount > 0)) return out;
  const payType = member.payType === 'fixed' ? 'fixed' : 'commission';
  const contractMax = contract.durationMonths > 0 ? contract.durationMonths : 600;
  const memberDur = Number(member.durationMonths) || 0;
  const maxMonths = memberDur > 0 ? Math.min(contractMax, memberDur) : contractMax;
  for (let i = 0; i < maxMonths; i++) {
    const { ms, periodKey } = dueDateForOffset(contract.startAt, i);
    if (ms > nowMs) break;
    if (sinceMs && ms < sinceMs) continue; // vor der Zuweisung -> keine Kosten
    out.push({ periodKey, type: 'recurring', payType, dueDate: ms, amount });
  }
  return out;
}

// Auszahlung EINES Mitglieds für GENAU EINE bereits BEZAHLTE Kundenrechnung.
// Zahlungsgesteuertes Modell (= die wahre Reihenfolge im Geschäft):
//   Rechnung fällig -> gestellt -> der Kunde ZAHLT -> erst dann wird die
//   Provision/Fixvergütung der im Auftrag beteiligten (= dem Kunden
//   zugewiesenen) Mitglieder fällig ("freigeschaltet").
// Liefert null, wenn keine Auszahlung entsteht. Sonst die fertige Auszahlung.
//   member     – Teammitglied (payType, commissionPercent/fixedAmount,
//                appointmentBonus, durationMonths, active)
//   contract   – Vertrag des Kunden (monthlyAmount, startAt, durationMonths)
//   payment    – die Rechnung (muss status 'paid' und type 'recurring' sein)
//   assignedAt – Zeitpunkt der Zuweisung des Mitglieds an den Kunden (ms)
// Hinweise:
//   • Keine rückwirkenden Auszahlungen: Rechnungen, die VOR der Zuweisung
//     bezahlt wurden, lösen für dieses Mitglied nichts aus.
//   • member.durationMonths begrenzt die Anzahl der bezahlten Monate (ab
//     Vertragsstart gerechnet), unabhängig von der Vertragslaufzeit.
//   • Termin-Vergütung: Ist am Vertrag das Provisionsmodell aktiv UND das
//     Mitglied einer Staffel zugeordnet, kommt die gestaffelte Provision aus
//     commissionCore auf die Provisionsbasis (NICHT auf den Kundenpreis) obendrauf.
//     Sonst greift wie bisher der pauschale Bonus je Extra-Termin.
//   roleOverride – am Kunden gewählte Provisionsrolle (customers/{slug}/teamRoles)
function payoutForPaidInvoice(member, contract, payment, assignedAt = 0, roleOverride = null) {
  if (!member || member.active === false) return null;
  if (!contract || !contract.startAt) return null;
  if (!payment || payment.status !== 'paid' || payment.type !== 'recurring') return null;
  const paidAt = typeof payment.paidAt === 'number' ? payment.paidAt : (Number(payment.dueDate) || 0);
  if (assignedAt && paidAt && assignedAt > paidAt) return null; // vor Zuweisung bezahlt
  const memberDur = Number(member.durationMonths) || 0;
  if (memberDur > 0) {
    const idx = monthsElapsed(contract.startAt, Number(payment.dueDate) || paidAt);
    if (idx >= memberDur) return null; // Vergütungslaufzeit des Mitglieds beendet
  }
  const base = memberMonthlyPayout(member, contract.monthlyAmount);
  const extra = Math.max(0, Math.round(Number(payment.extraAppointments)) || 0);
  const appointments = appointmentsOnPayment(payment, contract);
  const share = commission.commissionForMember(member, contract, appointments, roleOverride);
  // Staffel und Pauschalbonus schließen sich aus – sonst würde derselbe Termin
  // zweimal vergütet.
  const bonus = share ? 0 : (Number(member.appointmentBonus) || 0);
  const apptAmount = share ? share.amount : extra * bonus;
  const amount = base + apptAmount;
  if (!(amount > 0)) return null;
  const payType = member.payType === 'fixed' ? 'fixed' : 'commission';
  const out = {
    payType,
    periodKey: payment.periodKey,
    dueDate: Number(payment.dueDate) || paidAt,
    baseAmount: base,
    appointmentBonus: bonus,
    extraAppointments: extra,
    appointments,
    amount,
  };
  if (share) {
    out.commissionRole = share.roleId;
    out.commissionAmount = share.amount;
    out.commissionBase = share.commissionBase;
    out.commissionMinimumApplied = share.minimumApplied;
  }
  return out;
}

// ── Einmalzahlungen (Setup-Fee, Beratung, …) ──────────────────
// Am Kunden gepflegt (customers/{slug}/oneTimes/{id}), NICHT am Vertrag: eine
// Beratung kann auch ohne laufenden Retainer abgerechnet werden. Jede
// Einmalzahlung erzeugt genau EINE Rechnung – die deterministische ID
// `slug__once_<id>` macht den Generator idempotent, genau wie bei den
// Monatsrechnungen.
//
// Provisionen: je Einmalzahlung kann pro Teammitglied ein Anteil hinterlegt
// werden – entweder PROZENTUAL vom Netto oder als FIXER Betrag. Ausgezahlt wird
// wie überall erst, wenn der Kunde die Rechnung bezahlt hat.
const ONE_TIME_PREFIX = 'once_';
const oneTimePeriodKey = (oneTimeId) => `${ONE_TIME_PREFIX}${oneTimeId}`;
const isOneTimePeriodKey = (periodKey) =>
  typeof periodKey === 'string' && periodKey.indexOf(ONE_TIME_PREFIX) === 0;
const oneTimeIdFromPeriodKey = (periodKey) =>
  isOneTimePeriodKey(periodKey) ? periodKey.slice(ONE_TIME_PREFIX.length) : null;

const ONE_TIME_COMMISSION_MODES = ['percent', 'fixed'];

// Eine Einmalzahlung in eine stabile Form bringen (Beträge als Zahlen, Provisionen
// normalisiert). Liefert null, wenn der Eintrag unbrauchbar ist.
function normalizeOneTime(id, raw) {
  if (!id || !raw || typeof raw !== 'object') return null;
  const amount = Number(raw.amount);
  if (!isFinite(amount) || amount <= 0) return null;
  const commissions = {};
  const src = raw.commissions && typeof raw.commissions === 'object' ? raw.commissions : {};
  Object.keys(src).forEach((memberId) => {
    const c = src[memberId];
    if (!c || typeof c !== 'object') return;
    const mode = ONE_TIME_COMMISSION_MODES.indexOf(c.mode) !== -1 ? c.mode : 'percent';
    const value = Number(c.value);
    if (!isFinite(value) || value <= 0) return;
    commissions[memberId] = { mode, value };
  });
  // Beschreibung und Features sind eine KOPIE aus dem Setup-Paket, kein Verweis:
  // ändert sich das Paket später, bleibt verkauft, was verkauft wurde.
  const rawFeatures = Array.isArray(raw.features)
    ? raw.features
    : (raw.features && typeof raw.features === 'object'
      ? Object.keys(raw.features).sort().map((k) => raw.features[k])
      : []);
  return {
    id,
    label: typeof raw.label === 'string' && raw.label.trim() ? raw.label.trim() : 'Einmalzahlung',
    amount,
    dueDate: Number(raw.dueDate) || Number(raw.createdAt) || 0,
    cancelled: !!raw.cancelled,
    note: typeof raw.note === 'string' ? raw.note.trim() : '',
    features: rawFeatures.map((f) => (typeof f === 'string' ? f.trim() : '')).filter(Boolean),
    commissions,
  };
}

// Alle Einmalzahlungen eines Kunden, chronologisch. Stornierte bleiben enthalten
// (die Anzeige braucht sie), sind aber als `cancelled` markiert.
function oneTimeList(customer) {
  const src = customer && customer.oneTimes;
  if (!src || typeof src !== 'object') return [];
  return Object.keys(src)
    .map((id) => normalizeOneTime(id, src[id]))
    .filter(Boolean)
    .sort((a, b) => (a.dueDate || 0) - (b.dueDate || 0) || a.id.localeCompare(b.id));
}

const findOneTime = (customer, oneTimeId) =>
  oneTimeList(customer).find((o) => o.id === oneTimeId) || null;

// Bis JETZT fällige Einmalzahlungen als Rechnungsposten – das Gegenstück zu
// billingPeriods(). Bewusst unabhängig vom Vertragsstatus; der Vertrag liefert
// nur die steuerliche Behandlung (ohne Vertrag greifen die Standardregeln).
function oneTimePeriods(customer, nowMs = Date.now()) {
  const contract = (customer && customer.contract) || null;
  const rate = effectiveVatRate(contract);
  const taxMode = taxModeInfo(contract && contract.taxMode).id;
  const out = [];
  oneTimeList(customer).forEach((o) => {
    if (o.cancelled) return;
    if (!o.dueDate || o.dueDate > nowMs) return;
    const vat = o.amount * (rate / 100);
    out.push({
      periodKey: oneTimePeriodKey(o.id),
      type: 'onetime',
      oneTimeId: o.id,
      label: o.label,
      dueDate: o.dueDate,
      amount: o.amount + vat,
      net: o.amount,
      vat,
      vatRate: rate,
      taxMode,
    });
  });
  return out;
}

// Provisionsanteil EINES Mitglieds an EINER Einmalzahlung.
// Prozentsätze rechnen immer auf das NETTO – die Umsatzsteuer ist durchlaufender
// Posten und nie Teil der Vergütung.
function oneTimeCommissionFor(oneTime, memberId) {
  if (!oneTime || !memberId) return null;
  const c = oneTime.commissions && oneTime.commissions[memberId];
  if (!c) return null;
  const net = Number(oneTime.amount) || 0;
  const amount = c.mode === 'fixed'
    ? Math.min(c.value, net)          // ein Fixbetrag kann den Umsatz nie übersteigen
    : net * (Math.min(c.value, 100) / 100);
  if (!(amount > 0)) return null;
  return { mode: c.mode, value: c.value, base: net, amount };
}

// Summe aller Provisionen einer Einmalzahlung (für Vorschau und Marge).
function oneTimePayoutTotal(oneTime) {
  if (!oneTime || !oneTime.commissions) return 0;
  return Object.keys(oneTime.commissions).reduce((sum, memberId) => {
    const share = oneTimeCommissionFor(oneTime, memberId);
    return sum + (share ? share.amount : 0);
  }, 0);
}

// Auszahlung EINES Mitglieds für EINE bereits BEZAHLTE Einmalzahlungs-Rechnung.
// Gleiche Regel wie bei den Monatsrechnungen: erst wenn der Kunde gezahlt hat.
// Keine Zuweisungs-/Laufzeitprüfung – der Anteil wurde für genau diese Zahlung
// bewusst und namentlich hinterlegt.
function payoutForPaidOneTime(member, oneTime, payment, memberId) {
  if (!member || member.active === false) return null;
  if (!oneTime || oneTime.cancelled) return null;
  if (!payment || payment.status !== 'paid' || payment.type !== 'onetime') return null;
  const share = oneTimeCommissionFor(oneTime, memberId);
  if (!share) return null;
  return {
    payType: member.payType === 'fixed' ? 'fixed' : 'commission',
    periodKey: oneTimePeriodKey(oneTime.id),
    oneTimeId: oneTime.id,
    label: oneTime.label,
    dueDate: Number(payment.dueDate) || (typeof payment.paidAt === 'number' ? payment.paidAt : 0),
    amount: share.amount,
    baseAmount: share.amount,
    commissionMode: share.mode,
    commissionValue: share.value,
    commissionBase: share.base,
  };
}

// ── Monatliches Reporting (Outreach-Manager) ──────────────────
// Jede wiederkehrende Rechnung ist mit genau einem Reporting verknüpft
// (gleiche ID `slug__periodKey`). Ein Reporting deckt die 30 Tage VOR seinem
// Fälligkeitstag ab und wird GLEICHZEITIG mit der Monatsrechnung dieses
// Zeitraums freigeschaltet. Der Outreach-Manager hat danach 7 Tage Zeit.
//
// Termin-Kopplung: Die ersten INCLUDED_APPTS_PER_MONTH Termine sind im
// Fixbetrag enthalten. Was darüber hinaus als "qualifizierter Termin" im
// Reporting steht, landet automatisch als Extra-Termin auf der Rechnung:
//   extraTermine = max(0, qualifizierteTermine - INCLUDED_APPTS_PER_MONTH)
const INCLUDED_APPTS_PER_MONTH = 4;   // 1.–4. Termin inklusive, ab dem 5. extra
const REPORTING_WINDOW_DAYS = 7;      // Frist nach Freischaltung
const DAY_MS = 24 * 60 * 60 * 1000;

// Wie viele Termine deckt der Retainer dieses Vertrags ab? Pro Kunde
// einstellbar (contract.includedAppointments); ohne Angabe gilt der
// Standard. Bei reinem Provisionsmodell ist der Wert typischerweise 0,
// weil jeder Termin einzeln abgerechnet wird.
function includedAppointmentsFor(contract) {
  const own = contract && contract.includedAppointments;
  if (typeof own === 'number' && isFinite(own) && own >= 0) return Math.round(own);
  return INCLUDED_APPTS_PER_MONTH;
}

// Gesamtzahl der Termine einer Rechnung – Basis der Termin-Vergütung.
//
// `appointments` wird beim Eintragen mitgeschrieben. Für Altbestand wird aus den
// Extra-Terminen rekonstruiert (extra + inkludierte). Wurden für die Periode
// NOCH GAR KEINE Termine erfasst (kein Reporting, keine manuelle Eingabe),
// liefert die Funktion 0 – sonst würde das inkludierte Kontingent als
// tatsächlich geleistete Termine gelten und Provision auslösen, obwohl niemand
// einen Termin gemeldet hat.
function appointmentsOnPayment(payment, contract) {
  if (!payment) return 0;
  const total = Number(payment.appointments);
  if (isFinite(total) && total >= 0) return Math.round(total);
  if (typeof payment.extraAppointments !== 'number') return 0; // nie erfasst
  const extra = Math.max(0, Math.round(payment.extraAppointments) || 0);
  return extra + includedAppointmentsFor(contract);
}

// Deterministische ID eines Reportings = identisch zur verknüpften Rechnung.
const reportingId = (slug, periodKey) => `${slug}__${periodKey}`;

// Aus den qualifizierten Terminen die abrechenbaren Extra-Termine ableiten.
const extraFromQualified = (qualified, included = INCLUDED_APPTS_PER_MONTH) =>
  Math.max(0, (Math.round(Number(qualified)) || 0) - (Number(included) || 0));
// Aus den Extra-Terminen (Rechnung) die qualifizierten Termine ableiten (Gegenrichtung).
const qualifiedFromExtra = (extra, included = INCLUDED_APPTS_PER_MONTH) =>
  Math.max(0, (Math.round(Number(extra)) || 0)) + (Number(included) || 0);

// Alle relevanten Reporting-Perioden eines Vertrags: alle bereits
// freigeschalteten (availableAt <= now) PLUS die nächste anstehende
// (für den Countdown). Reporting i (i>=1) gehört zur i-ten Monatsrechnung.
function reportingPeriods(contract, nowMs = Date.now()) {
  const out = [];
  if (!contract || !contract.active || !contract.startAt) return out;
  if (!hasRecurringBilling(contract)) return out;
  const included = includedAppointmentsFor(contract);
  const contractMax = contract.durationMonths > 0 ? contract.durationMonths : 600;
  for (let i = 1; i < contractMax; i++) {
    const cur = dueDateForOffset(contract.startAt, i);
    const prev = dueDateForOffset(contract.startAt, i - 1);
    const entry = {
      periodKey: cur.periodKey,
      type: 'recurring',
      availableAt: cur.ms,
      dueDate: cur.ms,
      periodStart: prev.ms,
      periodEnd: cur.ms,
      deadlineAt: cur.ms + REPORTING_WINDOW_DAYS * DAY_MS,
      included,
    };
    out.push(entry);
    if (cur.ms > nowMs) break; // genau die nächste anstehende Periode noch mitnehmen
  }
  return out;
}

// Lebenszyklus-Status eines Reportings aus seinen Zeitpunkten ableiten.
//   scheduled -> Countdown läuft (noch nicht freigeschaltet)
//   open      -> freigeschaltet, innerhalb der 7-Tage-Frist
//   overdue   -> Frist überschritten, noch nicht abgegeben
//   submitted -> abgegeben
function reportingState(reporting, nowMs = Date.now()) {
  if (!reporting) return 'scheduled';
  if (reporting.status === 'submitted' || typeof reporting.submittedAt === 'number') return 'submitted';
  const avail = Number(reporting.availableAt) || 0;
  const deadline = Number(reporting.deadlineAt) || (avail + REPORTING_WINDOW_DAYS * DAY_MS);
  if (nowMs < avail) return 'scheduled';
  if (nowMs <= deadline) return 'open';
  return 'overdue';
}

module.exports = {
  DEFAULT_TAX_MODE,
  DEFAULT_VAT_RATE,
  TAX_MODES,
  taxModeInfo,
  effectiveVatRate,
  taxBreakdown,
  grossFromNet,
  SELLER_COUNTRY,
  EU_COUNTRIES,
  COUNTRY_NAMES,
  countryName,
  isEuCountry,
  taxModeForCountry,
  paymentId,
  dueDateForOffset,
  monthsElapsed,
  billingPeriods,
  hasRecurringBilling,
  nextDuePeriod,
  contractRunning,
  memberMonthlyPayout,
  payoutRemainingMonths,
  payoutId,
  payoutPeriods,
  payoutForPaidInvoice,
  ONE_TIME_PREFIX,
  ONE_TIME_COMMISSION_MODES,
  oneTimePeriodKey,
  isOneTimePeriodKey,
  oneTimeIdFromPeriodKey,
  normalizeOneTime,
  oneTimeList,
  findOneTime,
  oneTimePeriods,
  oneTimeCommissionFor,
  oneTimePayoutTotal,
  payoutForPaidOneTime,
  INCLUDED_APPTS_PER_MONTH,
  REPORTING_WINDOW_DAYS,
  reportingId,
  includedAppointmentsFor,
  appointmentsOnPayment,
  extraFromQualified,
  qualifiedFromExtra,
  reportingPeriods,
  reportingState,
  // Provisionsmodell (BonusClub-Staffel) – durchgereicht, damit Frontend und
  // Cloud Function nur EINEN Core importieren müssen.
  COMMISSION_ROLES: commission.COMMISSION_ROLES,
  COMMISSION_ROLE_IDS: commission.COMMISSION_ROLE_IDS,
  COMMISSION_PLAN_BASE: commission.COMMISSION_PLAN_BASE,
  commissionRoleInfo: commission.commissionRoleInfo,
  commissionActive: commission.commissionActive,
  commissionBaseFor: commission.commissionBaseFor,
  appointmentPriceFor: commission.appointmentPriceFor,
  commissionRoleForMember: commission.commissionRoleForMember,
  customerRoleOverride: commission.customerRoleOverride,
  memberCommissionMinimum: commission.memberCommissionMinimum,
  commissionForRole: commission.commissionForRole,
  commissionForMember: commission.commissionForMember,
  commissionBreakdown: commission.commissionBreakdown,
};
